/**
 * Uždarymo vedlys API — the closure wizard's spine. All endpoints exist on the
 * backend; the dry-run is always rolled back server-side (a full simulation).
 */
import { api } from './client';

export interface DiagnosticSnapshot {
  capturedAt: string;
  workspaceId: string;
  company: { code: string | null; name: string | null; baseCurrency: string | null; incomeClassPrefix: string | null; expenseClassPrefix: string | null };
  /** Typed blockers — the wizard renders Lithuanian states per code, never raw server prose. */
  period: { canClose: boolean; blockers: CloseBlocker[] };
  /** Wire shape = AccountBalanceDiagnostic (netBalance/accountClass — the old
   *  `balance`/`className` names never existed server-side and caused the zero wall). */
  accountBalances: Array<{ accountNumber: number; accountName: string; accountClass: string; netBalance: number }>;
  preCloseErrors: string[];
  preCloseWarnings: string[];
  totalJournalEntries: number;
  postedJournalEntries: number;
  unpostedJournalEntries: number;
  lastClosingDate: string | null;
  lastClosedPeriodDate: string | null;
  trialBalance: { totalDebits: number; totalCredits: number; imbalance: number; isBalanced: boolean };
  closingAccounts?: {
    configured: boolean;
    proposedClosingSummary: number | null;
    proposedCurrentProfit: number | null;
    proposedFormerProfit: number | null;
    proposedFromCloseDate: string | null;
  } | null;
  unpostedAfterCloseDate?: number;
}

export interface CloseBlocker {
  code: 'alreadyClosed' | 'dateInvalid' | 'unpostedOnOrBefore' | 'checklistErrors' | string;
  date: string | null;
  count: number | null;
}

export interface CloseDryRunResult {
  success: boolean;
  linesCreated: number;
  totalClosingDebits: number;
  totalClosingCredits: number;
  passedAssertions: string[];
  failedAssertions: string[];
  warnings: string[];
  error: string | null;
  profitAppropriationCompleted: boolean | null;
}

export const periodsApi = {
  diagnostic: (proposedCloseDate: string, isAnnualClose: boolean) =>
    api.get<DiagnosticSnapshot>('/periods/diagnostic', { proposedCloseDate, isAnnualClose }),

  autoCloseDryRun: (closeAtDate: string, isAnnualClose: boolean, agmApprovalDate?: string) =>
    api.post<CloseDryRunResult>('/periods/auto-close', {
      closeAtDate, isAnnualClose, dryRun: true,
      agmApprovalDate: agmApprovalDate ?? null,
    }),

  autoClose: (closeAtDate: string, isAnnualClose: boolean, agmApprovalDate?: string) =>
    api.post<number>('/periods/auto-close', {
      closeAtDate, isAnnualClose, dryRun: false,
      agmApprovalDate: agmApprovalDate ?? null,
    }),

  reopen: (closedPeriodDate: string, reason: string) =>
    api.post('/periods/reopen', { closedPeriodDate, reason }),
};

export interface PreCloseSnapshotDto {
  id: number;
  closeDate: string;
  closeType: string;
  balanceDataJson: string;
  turnoverDataJson: string;
  incomeStatementJson: string;
  createdAt: string;
}

export const companyAccountsApi = {
  set: (accountType: 'ClosingSummary' | 'CurrentProfit' | 'FormerProfit', accountNumber: number) =>
    api.put(`/company-accounts/${accountType}`, { accountNumber }),
};

export const snapshotsApi = {
  list: (closeDate?: string) =>
    api.get<PreCloseSnapshotDto[]>('/reports/pre-close-snapshots', closeDate ? { closeDate } : undefined),
};
